import _ from 'lodash';
import {closeGraphsView, error, info, populateAwsDataForGraphAsyncPromise} from '../actions';
import * as ActionTypes from '../actions/actionTypes';
import {geoserverBase, urlBase} from '../config/environment';
import exportConfig from '../config/export_config.json';
import {fetchJson} from '../utils/fetch_utils';
import {constructPointRequestUrl} from '../utils/point_utils';
import {securityHeaders} from '../utils/security_utils';
import {getNextVersion} from './asyncManagerMiddleware';
import {buildUrlHashFromState} from './locationMiddleware';

// We have a problem where during startup we get a number of actions that can result in the graph data getting updated.
// This causes a lot of extra load. So here I create a 5 second window where we delay the calls, and limit to just one.
let _inFirstFourSeconds = true;
let _updateGraphsQueued = false;
setTimeout(() => {_inFirstFourSeconds = false;}, 4000);

const _clearGraphData = (store) => {
  const dispatch = store.dispatch;
  const state = store.getState();

  // data_idx is incremented with each new request. Prevents the request overlapping.
  const data_idx = state.graphs.data_idx + 1;

  dispatch({
    type: ActionTypes.CLEAR_GRAPH_DATA,
    num_points: state.time.valid_times.length,
    data_idx
  });
};

const _updateGraphsAsync = (store) => {
  const dispatch = store.dispatch;
  const state = store.getState();
  const { point, indicators, aws, graphs, app } = state;

  if (!point.lat) {
    return;
  }

  const graphPrefixes = [];
  state.point_data_weather.graph_meta_data.forEach(md => md.series.forEach(s => graphPrefixes.push(s.dataKey)));

  // Use a set to remove duplicates
  const exportPrefixes = Array.from(new Set([
    ...exportConfig.indicatorKeys_simple_wildfire,
    ...exportConfig.indicatorKeys_simple_plannedBurns,
    ...exportConfig.indicatorKeys_detailed_wildfire,
    ...exportConfig.indicatorKeys_detailed_plannedBurns
  ]));

  let prefixes = [];
  state.indicators.indicators.forEach((ind) => {
    const pointDataRequired = ind.point_data &&
                              (!ind.hide_in_table ||
                               ind.include_in_isp ||
                               graphPrefixes.includes(ind.prefix) ||
                               exportPrefixes.includes(ind.prefix));
    const indicatorAllowed = !ind.only_in_app_mode || ind.only_in_app_mode === app.app_mode;
    if (pointDataRequired && indicatorAllowed && ind.available) {
      prefixes.push(ind.prefix);
    }
  });
  prefixes = _.uniq(_.compact(prefixes));

  // This might happen if the metadata has not yet been loaded.
  if (prefixes.length === 0) {
    return;
  }

  const indicatorMap = _.keyBy(indicators.indicators, 'prefix');

  const baseMap = {};
  prefixes.forEach((prefix) => {
    const indicator = indicatorMap[prefix];
    if (!indicator) {
      throw new Error('Graph has dataKey that does not match an Indicator: ' + prefix);
    }

    let mapKey = indicator.base || geoserverBase;
    if (baseMap[mapKey]) {
      baseMap[mapKey].push(prefix);
    } else {
      baseMap[mapKey] = [prefix];
    }
  });

  // data_idx is incremented with each new request. Prevents the request overlapping.
  const data_idx = graphs.data_idx + 1;

  dispatch({
    type: ActionTypes.CLEAR_GRAPH_DATA,
    num_points: state.time.valid_times.length * _.keys(baseMap).length,
    data_idx
  });

  _setProgressTimerTick(store, true);

  const promises = [];

  if (aws.active) {
    promises.push(dispatch(populateAwsDataForGraphAsyncPromise(aws.active, data_idx)));
  }

  const asyncVersion = getNextVersion(ActionTypes.POPULATE_INDICATOR_GRAPH_POINT);

  _.keys(baseMap).forEach((key) => {
    const prefixes = baseMap[key];
    const source = indicatorMap[prefixes[0]].source;
    const securityKey = indicatorMap[prefixes[0]].securityKey;
    const headers = securityKey ? securityHeaders(securityKey, state) : {};
    const base = key.includes('|') ? key.substring(0, key.indexOf('|')) : key;

    state.time.valid_times.forEach((time) => {
      promises.push(_loadIndicatorPointsAsyncPromise(dispatch,
        base,
        source,
        point.lat,
        point.lng,
        time,
        prefixes,
        data_idx,
        asyncVersion,
        headers));
    });
  });

  Promise.all(promises).then(() => dispatch({
    type: ActionTypes.GRAPH_DATA_LOADED,
    include_history: graphs.include_history,
    short_future: graphs.short_future,
    data_idx
  }));
};

const _setProgressTimerTick = (store, force) => {
  if (force || !store.getState().graphs.graphs_loaded) {
    setTimeout(() => {
      store.dispatch({ type: ActionTypes.GRAPH_PROGRESS_TICK });
      _setProgressTimerTick(store);
    }, 1000);
  }
};

const _loadIndicatorPointsAsyncPromise = (dispatch,
                                          base,
                                          source,
                                          lat,
                                          lng,
                                          time,
                                          prefixes,
                                          data_idx,
                                          asyncVersion,
                                          headers) => {
  const url = constructPointRequestUrl(base, source, lat, lng, time, prefixes);
  return fetchJson(url, { headers })
  .then(data => dispatch(populateIndicatorGraphPoint(time, prefixes, data, data_idx, asyncVersion)))
  .catch(e => {
    dispatch(error(e.message));
  });
};

export const populateIndicatorGraphPoint = (time, prefixes, data, data_idx, asyncVersion) => {
  return {
    type: ActionTypes.POPULATE_INDICATOR_GRAPH_POINT,
    asyncVersion, time, prefixes, data, data_idx
  };
};

function _openGraphsWindow(post_state, print_on_load) {
  const slash = urlBase && !urlBase.endsWith('/') ? '/' : '';
  const url = `${urlBase}${slash}#${buildUrlHashFromState({
    ...post_state,
    view: {
      ...post_state.view,
      graph_view_expanded: true,
      pop_out: true,
      print_on_load
    }
  })}`;
  window.open(url, 'foresightPopout', 'popup=1');
}

export const orchestrateGraphData = store => next => action => {
  const starting_state = store.getState();
  const starting_app_mode = starting_state.app.app_mode;

  switch (action.type) {
    case ActionTypes.SET_ACTIVE_POINT:
      if (starting_state.view.graphs_open && !starting_state.graphs.graphs_loaded) {
        store.dispatch(info('Please wait for graph data to load before selecting a new point on the map.'));
        // Consume the action. Num num num.
        return;
      }
      break;

    default:
      break;
  }

  // First complete the current action
  const result = next(action);
  const post_state = store.getState();

  switch (action.type) {
    case ActionTypes.OPEN_GRAPHS_VIEW:
      if (!post_state.graphs.graphs_loaded) {
        _updateGraphsAsync(store);
      }
      break;

    case ActionTypes.POPULATE_INDICATOR_DETAILS:
      if (post_state.view.graphs_open) {
        if (!_inFirstFourSeconds) {
          _updateGraphsAsync(store);
        } else if (!_updateGraphsQueued) {
          _updateGraphsQueued = true;
          setTimeout(() => {_updateGraphsAsync(store);}, 4000);
        }
      }
      break;

    case ActionTypes.SET_ACTIVE_POINT:
      if (post_state.view.graphs_open) {
        _updateGraphsAsync(store);
      } else {
        _clearGraphData(store);
      }
      break;

    case ActionTypes.SET_APP_MODE:
      if (post_state.view.graphs_open && post_state.app.app_mode !== starting_app_mode) {
        _updateGraphsAsync(store);
      }
      break;

    case ActionTypes.TOGGLE_GRAPHS_DAYS:
    case ActionTypes.TOGGLE_GRAPHS_HISTORY:
    case ActionTypes.TOGGLE_TIMELINE_HISTORY:
      const { graphs } = post_state;
      if (graphs.graphs_loaded) {
        store.dispatch({
          type: ActionTypes.REBUILD_GRAPH_DATA,
          include_history: graphs.include_history,
          short_future: graphs.short_future,
          data_idx: graphs.data_idx
        });
      }
      break;

    case ActionTypes.EJECT_GRAPHS_VIEW:
      _openGraphsWindow(post_state, false);
      store.dispatch(closeGraphsView());
      break;

    case ActionTypes.PRINT_GRAPHS_VIEW:
      _openGraphsWindow(post_state, true);
      break;

    case ActionTypes.GRAPH_DATA_LOADED:
      // Print the page if in print mode
      if (post_state.view.print_on_load) {
        setTimeout(() => window.print(), 500);
      }
      break;

    default:
      break;
  }

  return result;
};



// WEBPACK FOOTER //
// ./src/middleware/graphsMiddleware.js